/**
 * alunos.contract.ts — Contrato semântico do domínio Alunos
 *
 * Define todas as rotas API, campos de formulário, tabelas/campos de persistência,
 * validações, máscaras e metadados que a IA precisa para entender o módulo de Alunos.
 *
 * O componente AlunoForm.tsx usa apenas id="aluno-form-*" nos elementos.
 * A IA consulta AcRegistry.get("aluno-form-*") para obter estes metadados.
 *
 * @internal true
 * @owner architecture
 */
import type { AcDomainContract } from './types';

export const ALUNOS_CONTRACT: AcDomainContract = {
  domain: 'alunos',
  name: 'Alunos',
  desc: 'Gestão de alunos matriculados na escola. Wizard de 6 etapas: Dados Pessoais, Acadêmico, Endereço, Saúde, Responsável e RFID/LGPD. Inclui validação de CPF em tempo real, busca de responsável existente, auto-geração de matrícula, upload de foto com crop, rascunho em localStorage e consentimento LGPD obrigatório.',
  icon: 'Users',
  table: 'Aluno',
  scope: ['master', 'admin', 'escola'],
  relations: ['Turma', 'TagRfid', 'ResponsavelAluno', 'AlunoTurma', 'PresencaDiaria', 'ChamadaAluno', 'NotaAluno'],
  wizardSteps: ['Dados Pessoais', 'Acadêmico', 'Endereço', 'Saúde', 'Responsável', 'RFID'],

  routes: {
    listar: {
      method: 'GET',
      path: 'escolas/:escolaId/alunos',
      desc: 'Lista todos os alunos da escola com filtros opcionais',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['escolaId'],
    },
    findOne: {
      method: 'GET',
      path: 'escolas/:escolaId/alunos/:alunoId',
      desc: 'Busca aluno pelo ID com responsáveis vinculados e tags RFID',
      scope: ['master', 'admin', 'escola', 'professor'],
      table: 'Aluno',
      params: ['escolaId', 'alunoId'],
    },
    criar: {
      method: 'POST',
      path: 'escolas/:escolaId/alunos',
      desc: 'Cria aluno com validação de CPF, vinculação de responsável e auto-geração de matrícula',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['escolaId'],
    },
    atualizar: {
      method: 'PUT',
      path: 'escolas/:escolaId/alunos/:alunoId',
      desc: 'Atualiza dados do aluno existente',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['escolaId', 'alunoId'],
    },
    checkDuplicidade: {
      method: 'POST',
      path: 'escolas/:escolaId/alunos/check-duplicidade',
      desc: 'Verifica se já existe aluno com mesmo CPF ou combinação nome+data de nascimento',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['escolaId'],
    },
    uploadFoto: {
      method: 'POST',
      path: 'upload/foto-aluno/:id',
      desc: 'Upload de foto do aluno em base64 com crop (JPEG/PNG/WebP, max 5MB)',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['id'],
    },
    termoLGPD: {
      method: 'GET',
      path: 'escolas/:escolaId/alunos/:alunoId/pdf/termo-lgpd',
      desc: 'Gera PDF do termo de consentimento LGPD para assinatura do responsável',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['escolaId', 'alunoId'],
    },
    validarCpf: {
      method: 'GET',
      path: 'validacoes/cpf/:cpf',
      desc: 'Valida se CPF já está em uso por outro aluno, responsável ou usuário',
      scope: ['master', 'admin', 'escola'],
      table: 'Aluno',
      params: ['cpf'],
    },
  },

  fields: {
    // ═══════════════════════════════════════════════
    // Step 0: Dados Pessoais
    // ═══════════════════════════════════════════════
    nome: {
      id: 'aluno-form-nome',
      name: 'Nome do Aluno',
      desc: 'Nome completo do aluno para matrícula. Mínimo 3 caracteres',
      type: 'input-text',
      table: 'Aluno',
      field: 'nome',
      required: true,
      persist: 'api',
      step: 0,
    },
    dataNascimento: {
      id: 'aluno-form-dt-nasc',
      name: 'Data de Nascimento',
      desc: 'Data de nascimento do aluno. A idade é calculada automaticamente e exibida ao lado',
      type: 'input-date',
      table: 'Aluno',
      field: 'dataNascimento',
      required: true,
      persist: 'api',
      step: 0,
    },
    genero: {
      id: 'aluno-form-genero',
      name: 'Gênero',
      desc: 'Gênero do aluno conforme classificação MEC',
      type: 'select-enum',
      table: 'Aluno',
      field: 'genero',
      enumName: 'TipoGenero',
      enumValues: ['MASCULINO', 'FEMININO', 'NAO_INFORMADO'],
      persist: 'api',
      step: 0,
    },
    cpf: {
      id: 'aluno-form-cpf',
      name: 'CPF do Aluno',
      desc: 'CPF com máscara automática. Validado em tempo real: dígitos verificadores + consulta de duplicidade no banco via API',
      type: 'input-cpf',
      table: 'Aluno',
      field: 'cpf',
      mask: 'cpf',
      validation: 'cpf-digitos,api-duplicidade',
      persist: 'api',
      step: 0,
    },
    corRaca: {
      id: 'aluno-form-cor-raca',
      name: 'Cor/Raça',
      desc: 'Classificação étnico-racial conforme padrão IBGE/MEC',
      type: 'select-enum',
      table: 'Aluno',
      field: 'corRaca',
      enumName: 'CorRaca',
      enumValues: ['BRANCA', 'PRETA', 'PARDA', 'AMARELA', 'INDIGENA', 'NAO_DECLARADO'],
      persist: 'api',
      step: 0,
    },
    nacionalidade: {
      id: 'aluno-form-nacionalidade',
      name: 'Nacionalidade',
      desc: 'Nacionalidade do aluno. Se estrangeiro, habilita campo de país de origem',
      type: 'select-enum',
      table: 'Aluno',
      field: 'nacionalidade',
      enumName: 'Nacionalidade',
      enumValues: ['BRASILEIRO_NATO', 'NATURALIZADO', 'ESTRANGEIRO'],
      persist: 'api',
      step: 0,
    },
    paisOrigem: {
      id: 'aluno-form-pais-origem',
      name: 'País de Origem',
      desc: 'País de origem quando nacionalidade é ESTRANGEIRO',
      type: 'input-text',
      table: 'Aluno',
      field: 'paisOrigem',
      persist: 'api',
      step: 0,
    },
    nis: {
      id: 'aluno-form-nis',
      name: 'NIS',
      desc: 'Número de Identificação Social para programas sociais (Bolsa Família, etc.)',
      type: 'input-text',
      table: 'Aluno',
      field: 'nis',
      persist: 'api',
      step: 0,
    },

    // ═══════════════════════════════════════════════
    // Step 1: Acadêmico
    // ═══════════════════════════════════════════════
    filtroTurno: {
      id: 'aluno-form-filtro-turno',
      name: 'Filtro por Turno',
      desc: 'Filtra turmas disponíveis pelo turno operacional',
      type: 'select',
      step: 1,
    },
    filtroSerie: {
      id: 'aluno-form-filtro-serie',
      name: 'Filtro por Série',
      desc: 'Filtra turmas disponíveis pela série/ano escolar',
      type: 'select',
      step: 1,
    },
    turmaId: {
      id: 'aluno-form-turma',
      name: 'Turma',
      desc: 'Turma onde o aluno será matriculado. Mostra capacidade e vagas restantes. Bloqueia seleção se turma lotada',
      type: 'select',
      table: 'Aluno',
      field: 'turmaId',
      required: true,
      persist: 'api',
      step: 1,
    },
    matricula: {
      id: 'aluno-form-matricula',
      name: 'Número de Matrícula',
      desc: 'Número de matrícula do aluno. Se deixado vazio, é gerado automaticamente pelo backend no formato sequencial',
      type: 'input-text',
      table: 'Aluno',
      field: 'matricula',
      persist: 'api',
      step: 1,
    },

    // ═══════════════════════════════════════════════
    // Step 2: Endereço
    // ═══════════════════════════════════════════════
    cep: {
      id: 'aluno-form-cep',
      name: 'CEP',
      desc: 'CEP do endereço. Ao preencher, busca automática via API ViaCEP e preenche rua, bairro, cidade e UF',
      type: 'input-cep',
      table: 'Aluno',
      field: 'cep',
      mask: 'cep',
      persist: 'api',
      step: 2,
    },
    endereco: {
      id: 'aluno-form-endereco',
      name: 'Endereço',
      desc: 'Logradouro completo (rua, avenida, etc.)',
      type: 'input-text',
      table: 'Aluno',
      field: 'endereco',
      persist: 'api',
      step: 2,
    },
    bairro: {
      id: 'aluno-form-bairro',
      name: 'Bairro',
      desc: 'Bairro do endereço do aluno',
      type: 'input-text',
      table: 'Aluno',
      field: 'bairro',
      persist: 'api',
      step: 2,
    },
    cidade: {
      id: 'aluno-form-cidade',
      name: 'Cidade',
      desc: 'Cidade do endereço do aluno',
      type: 'input-text',
      table: 'Aluno',
      field: 'cidade',
      persist: 'api',
      step: 2,
    },
    uf: {
      id: 'aluno-form-uf',
      name: 'UF',
      desc: 'Estado (unidade federativa) do endereço',
      type: 'input-text',
      table: 'Aluno',
      field: 'uf',
      persist: 'api',
      step: 2,
    },
    complemento: {
      id: 'aluno-form-complemento',
      name: 'Complemento',
      desc: 'Complemento do endereço (apto, bloco, etc.)',
      type: 'input-text',
      table: 'Aluno',
      field: 'complemento',
      persist: 'api',
      step: 2,
    },

    // ═══════════════════════════════════════════════
    // Step 3: Saúde
    // ═══════════════════════════════════════════════
    alergias: {
      id: 'aluno-form-alergias',
      name: 'Alergias',
      desc: 'Alergias conhecidas do aluno (alimentares, medicamentosas, etc.)',
      type: 'textarea',
      table: 'Aluno',
      field: 'alergias',
      persist: 'api',
      step: 3,
    },
    tipoSanguineo: {
      id: 'aluno-form-tipo-sang',
      name: 'Tipo Sanguíneo',
      desc: 'Tipo sanguíneo do aluno (A+, A-, B+, B-, AB+, AB-, O+, O-)',
      type: 'select',
      table: 'Aluno',
      field: 'tipoSanguineo',
      persist: 'api',
      step: 3,
    },
    restricaoAlimentar: {
      id: 'aluno-form-restricao-alim',
      name: 'Restrição Alimentar',
      desc: 'Restrições alimentares (vegetariano, intolerância a lactose, celíaco, etc.)',
      type: 'textarea',
      table: 'Aluno',
      field: 'restricaoAlimentar',
      persist: 'api',
      step: 3,
    },
    planoSaude: {
      id: 'aluno-form-plano-saude',
      name: 'Plano de Saúde',
      desc: 'Nome do plano de saúde do aluno (se houver)',
      type: 'input-text',
      table: 'Aluno',
      field: 'planoSaude',
      persist: 'api',
      step: 3,
    },
    tipoDeficiencia: {
      id: 'aluno-form-deficiencia',
      name: 'Tipo de Deficiência',
      desc: 'Tipo de deficiência conforme classificação MEC/PNE. Se selecionado, habilita campo de detalhamento',
      type: 'select-enum',
      table: 'Aluno',
      field: 'tipoDeficiencia',
      enumName: 'TipoDeficiencia',
      persist: 'api',
      step: 3,
    },
    deficienciaDetalhe: {
      id: 'aluno-form-deficiencia-detalhe',
      name: 'Detalhamento da Deficiência',
      desc: 'Detalhes adicionais sobre a deficiência (laudos, necessidades especiais, adaptações)',
      type: 'textarea',
      table: 'Aluno',
      field: 'deficienciaDetalhe',
      persist: 'api',
      step: 3,
    },

    // ═══════════════════════════════════════════════
    // Step 4: Responsável
    // ═══════════════════════════════════════════════
    respBusca: {
      id: 'aluno-form-resp-busca',
      name: 'Buscar Responsável',
      desc: 'Busca responsável já cadastrado na escola por nome, CPF ou telefone. Mínimo 3 caracteres. Debounce de 400ms',
      type: 'input-search',
      validation: 'min-3-chars',
      step: 4,
    },
    respNome: {
      id: 'aluno-form-resp-nome',
      name: 'Nome do Responsável',
      desc: 'Nome completo do responsável legal do aluno',
      type: 'input-text',
      table: 'Responsavel',
      field: 'nome',
      required: true,
      persist: 'api',
      step: 4,
    },
    respEmail: {
      id: 'aluno-form-resp-email',
      name: 'E-mail do Responsável',
      desc: 'E-mail para comunicados e notificações',
      type: 'input-email',
      table: 'Responsavel',
      field: 'email',
      persist: 'api',
      step: 4,
    },
    respTelefone: {
      id: 'aluno-form-resp-tel',
      name: 'Telefone do Responsável',
      desc: 'Telefone para contato e notificações push. Máscara automática (XX) XXXXX-XXXX',
      type: 'input-phone',
      table: 'Responsavel',
      field: 'telefone',
      mask: 'phone',
      persist: 'api',
      step: 4,
    },
    respCpf: {
      id: 'aluno-form-resp-cpf',
      name: 'CPF do Responsável',
      desc: 'CPF do responsável. Quando preenchido, verifica se já cadastrado para vincular automaticamente',
      type: 'input-cpf',
      table: 'Responsavel',
      field: 'cpf',
      mask: 'cpf',
      persist: 'api',
      step: 4,
    },
    respDtNasc: {
      id: 'aluno-form-resp-dt-nasc',
      name: 'Data Nasc. Responsável',
      desc: 'Data de nascimento do responsável',
      type: 'input-date',
      table: 'Responsavel',
      field: 'dataNascimento',
      persist: 'api',
      step: 4,
    },
    respParentesco: {
      id: 'aluno-form-resp-parentesco',
      name: 'Grau de Parentesco',
      desc: 'Relação do responsável com o aluno (Pai, Mãe, Avô, Tio, etc.)',
      type: 'select',
      table: 'ResponsavelAluno',
      field: 'grauParentesco',
      persist: 'api',
      step: 4,
    },
    respPrincipal: {
      id: 'aluno-form-resp-principal',
      name: 'Responsável Principal',
      desc: 'Marca este responsável como o principal para contato',
      type: 'checkbox',
      table: 'ResponsavelAluno',
      field: 'principal',
      persist: 'api',
      step: 4,
    },
    btnAddResp: {
      id: 'aluno-form-btn-add-resp',
      name: 'Adicionar Responsável',
      desc: 'Adiciona um novo card de responsável ao formulário',
      type: 'button-action',
      step: 4,
    },
    btnRemoveResp: {
      id: 'aluno-form-btn-remove-resp',
      name: 'Remover Responsável',
      desc: 'Remove responsável da lista (se ficar vazio, um card limpo é adicionado)',
      type: 'button-action',
      step: 4,
    },

    // ═══════════════════════════════════════════════
    // Step 5: RFID + LGPD
    // ═══════════════════════════════════════════════
    rfidCodigo: {
      id: 'aluno-form-rfid',
      name: 'Código RFID',
      desc: 'Código da tag RFID para controle de acesso físico na portaria da escola',
      type: 'input-text',
      table: 'TagRfid',
      field: 'codigo',
      persist: 'api',
      step: 5,
    },
    consentimentoLGPD: {
      id: 'aluno-form-lgpd',
      name: 'Consentimento LGPD',
      desc: 'Aceite obrigatório do responsável para coleta, tratamento e armazenamento de dados pessoais conforme Lei Geral de Proteção de Dados. Sem aceite, matrícula não é confirmada',
      type: 'checkbox',
      table: 'Aluno',
      field: 'consentimentoLGPD',
      required: true,
      persist: 'api',
      step: 5,
    },

    // ═══════════════════════════════════════════════
    // Botões de ação
    // ═══════════════════════════════════════════════
    btnSalvar: {
      id: 'aluno-form-btn-salvar',
      name: 'Confirmar Matrícula / Salvar',
      desc: 'Envia formulário para criar novo aluno ou salvar alterações. Valida CPF e LGPD antes de enviar',
      type: 'button-submit',
    },
    btnProximo: {
      id: 'aluno-form-btn-proximo',
      name: 'Próximo',
      desc: 'Avança para a próxima etapa do wizard. Na etapa 0, verifica duplicidade antes de avançar',
      type: 'button-wizard-next',
    },
    btnVoltar: {
      id: 'aluno-form-btn-voltar',
      name: 'Voltar',
      desc: 'Retorna para a etapa anterior do wizard',
      type: 'button-wizard-prev',
    },
    btnCancelar: {
      id: 'aluno-form-btn-cancelar',
      name: 'Cancelar',
      desc: 'Fecha o formulário sem salvar. Rascunho permanece em localStorage',
      type: 'button-action',
    },
    btnFoto: {
      id: 'aluno-form-btn-foto',
      name: 'Alterar Foto',
      desc: 'Abre seletor de arquivo para foto do aluno. Suporta JPEG, PNG e WebP até 5MB. Abre crop antes de enviar',
      type: 'file-upload',
    },
    btnTermoLGPD: {
      id: 'aluno-form-btn-termo-lgpd',
      name: 'Baixar Termo LGPD',
      desc: 'Gera e baixa PDF do termo de consentimento LGPD para impressão e assinatura',
      type: 'button-action',
    },
    btnQrCode: {
      id: 'aluno-form-btn-qrcode',
      name: 'Ver QR Code Ativação',
      desc: 'Exibe QR Code com link de ativação para o responsável configurar acesso ao app',
      type: 'button-action',
    },

    // ═══════════════════════════════════════════════
    // Rascunhos
    // ═══════════════════════════════════════════════
    btnRetomar: {
      id: 'aluno-form-btn-retomar',
      name: 'Retomar Rascunho',
      desc: 'Retoma uma matrícula interrompida salva em localStorage',
      type: 'button-action',
    },
    btnNovo: {
      id: 'aluno-form-btn-novo',
      name: 'Nova Matrícula',
      desc: 'Inicia uma nova matrícula do zero, ignorando rascunhos existentes',
      type: 'button-action',
    },
    btnRemoverRascunho: {
      id: 'aluno-form-btn-remover-rascunho',
      name: 'Remover Rascunho',
      desc: 'Exclui um rascunho de matrícula salvo em localStorage',
      type: 'button-action',
    },
  },
};
